import { StyledButton, StyledForm } from '@/components/styles'
import { FormField, AttributionHiddenFields } from '@/components'
import { useForm, FormContext } from 'react-hook-form'
import { useState, useRef, useEffect } from 'react'
import { normalizePhone } from '@/lib/config'
import { phoneValidateRules } from '@/lib/validatePhone'
import { useModal } from './Modal'
import api from '@/lib/api'
import { useTranslation } from "react-i18next";
import { getFormAttributionPayload, getSessionCode, getPagePath, createSubmissionId } from '@/lib/attribution'
import { getFormSubmitErrorMessage } from '@/lib/formSubmitError'

export default function ConsultationForm() {
  const formMethods = useForm()
  const { handleSubmit, reset } = formMethods
  const { t } = useTranslation("global");

  const [submissionId] = useState(createSubmissionId)
  const [status, setStatus] = useState({
    success: false,
    loading: false,
    error: '',
  })
  const errorInput = useRef()

  useEffect(() => {
    if (status.error && errorInput.current) {
      errorInput.current.focus()
    }
  }, [status.error])

  const finishWithError = (error) => {
    setStatus((prevStatus) => ({
      ...prevStatus,
      loading: false,
      error: getFormSubmitErrorMessage(error),
    }))
  }

  const onFormSubmit = async data => {
    setStatus(prevStatus => ({ ...prevStatus, loading: true, error: '' }))

    try {
      const resp = await api.consultationCreate({
        ...data,
        phone: normalizePhone(data.phone),
        ...getFormAttributionPayload(),
        sid: getSessionCode(),
        form_id: 'consultation',
        page_path: getPagePath(),
        submission_id: submissionId,
      })
      if (resp.result === 'success') {
        reset()
        setStatus(prevStatus => ({ ...prevStatus, loading: false, success: true }))
      } else {
        finishWithError()
      }
    } catch (error) {
      finishWithError(error)
    }
  }

  return status.success ? (
    <Success />
  ) : (
    <FormContext {...formMethods}>
      <StyledForm
        as="form"
        action="/"
        onSubmit={handleSubmit(onFormSubmit)}
        modal
        autoComplete="off"
      >
        <AttributionHiddenFields />
        <StyledForm.Title>{t('consultationForm.title')}</StyledForm.Title>

        {status.error && (
          <StyledForm.Error ref={errorInput} tabIndex={-1}>
            {status.error}
          </StyledForm.Error>
        )}

        <StyledForm.Block>
          <StyledForm.Row>
            <StyledForm.Item>
              <FormField label={t('consultationForm.name')} name="name" validateRules={{ required: true }} autoFocus />
            </StyledForm.Item>

            <StyledForm.Item>
              <FormField
                label={t('consultationForm.phone')}
                name="phone"
                type="tel"
                inputmode="tel"
                autoComplete="tel"
                placeholder="050 123 4567"
                validateRules={phoneValidateRules}
              />
            </StyledForm.Item>
          </StyledForm.Row>

          <StyledForm.Row>
            <StyledForm.Item>
              <FormField inputAs="textarea" label={t('consultationForm.comment')} name="comment" />
            </StyledForm.Item>
          </StyledForm.Row>
        </StyledForm.Block>

        <StyledForm.Submit>
          <StyledButton type="submit" disabled={status.loading} isLoading={status.loading}>
          {t('consultationForm.submit')}
            <StyledButton.Loader />
          </StyledButton>
        </StyledForm.Submit>
      </StyledForm>
    </FormContext>
  )
}

function Success() {
  const modal = useModal()
  const { closeModal } = modal
  const { t } = useTranslation("global");

  return (
    <StyledForm as="div" modal>
      <StyledForm.Title centered>{t('consultationForm.thanks.title')}</StyledForm.Title>
      <StyledForm.Desc centered>{t('consultationForm.thanks.desc')}</StyledForm.Desc>
      <StyledForm.Submit>
        <StyledButton type="button" onClick={closeModal}>
        {t('consultationForm.thanks.button')}
        </StyledButton>
      </StyledForm.Submit>
    </StyledForm>
  )
}
